import { createNumberTicker } from './number-ticker.js';
import { createCollectionResponseCache } from './collection-response-cache.js?v=20261008-filter-performance';
import { fetchCollectionText } from './collection-request.js?v=20261008-filter-performance';

// Reuse the content-versioned entry to avoid a second, stale module instance.
const collectionBrowsingUrl = document.querySelector('script[src*="/assets/collection-browsing.js"]')?.src
  || new URL('./collection-browsing.js?v=20261008-filter-performance', import.meta.url).href;
const { updateCollectionView } = await import(collectionBrowsingUrl);

const filterReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const FILTER_DRAWER_MOTION = {
  enterDuration: 260,
  exitDuration: 200,
  enterOffset: 'clamp(24px, 6vw, 64px)',
  exitOffset: 'clamp(20px, 5vw, 48px)',
  enterEasing: 'cubic-bezier(0.23, 1, 0.32, 1)',
  exitEasing: 'cubic-bezier(0.215, 0.61, 0.355, 1)',
};
const FILTER_TEXT_SWAP_DURATION = 150;
const FILTER_ACTION_MIN_LOADING_DURATION = 180;
const FILTER_RESULT_MIN_LOADING_DURATION = 260;
const FILTER_DISCLOSURE_CLOSE_FALLBACK = 260;
const FILTER_PREVIEW_CACHE_TTL = 10_000;

/* ─────────────────────────────────────────────────────────
 * FILTER ACTION STORYBOARD
 *
 *   0ms   changed filters crossfade the check icon into a spinner
 * 200ms   spinner is fully sharp while the Apply label stays stable
 * 260ms   earliest result label transition into Show X coffees
 *   0ms   confirmed clear replaces its label with a loader
 * result  selections remain cleared and the drawer stays open
 *   0ms   submitted results close the sheet and update the collection in place
 * ───────────────────────────────────────────────────────── */

const FILTER_FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const visibleFilterFocusables = (container) =>
  [...container.querySelectorAll(FILTER_FOCUSABLE_SELECTOR)].filter(
    (element) =>
      element instanceof HTMLElement &&
      element.getAttribute('aria-disabled') !== 'true' &&
      element.getAttribute('aria-hidden') !== 'true' &&
      !element.closest('[inert]') &&
      element.getClientRects().length > 0,
  );

const initializedFilterDrawers = new WeakMap();
const initializedFilterDisclosures = new WeakSet();

const initializeFilterDisclosures = (drawer) => {
  const disclosures = [...drawer.querySelectorAll('details')]
    .map((details) => {
      if (initializedFilterDisclosures.has(details)) {
        return null;
      }

      const summary = [...details.children].find((child) => child.matches('summary'));
      const motion = [...details.children].find((child) =>
        child.matches('.collection-filter-motion'),
      );

      if (
        !(details instanceof HTMLDetailsElement) ||
        !(summary instanceof HTMLElement) ||
        !(motion instanceof HTMLElement)
      ) {
        return null;
      }

      const group = details.getAttribute('name') || '';

      if (group) {
        details.dataset.collectionFilterDisclosureGroup = group;
        details.removeAttribute('name');
      }

      details.dataset.collectionFilterMotionState = details.open ? 'open' : 'closed';
      motion.inert = !details.open;
      initializedFilterDisclosures.add(details);

      return { details, summary, motion, frame: 0, closeTimer: 0 };
    })
    .filter(Boolean);

  const clearScheduledMotion = (disclosure) => {
    window.cancelAnimationFrame(disclosure.frame);
    window.clearTimeout(disclosure.closeTimer);
    disclosure.frame = 0;
    disclosure.closeTimer = 0;
  };

  const finishClose = (disclosure) => {
    if (disclosure.details.dataset.collectionFilterMotionState !== 'closing') {
      return;
    }

    clearScheduledMotion(disclosure);
    disclosure.details.open = false;
    disclosure.details.dataset.collectionFilterMotionState = 'closed';
    disclosure.motion.inert = true;
  };

  const closeDisclosure = (disclosure, { immediate = false } = {}) => {
    clearScheduledMotion(disclosure);
    disclosure.motion.inert = true;

    if (!disclosure.details.open || immediate || filterReducedMotion.matches) {
      disclosure.details.open = false;
      disclosure.details.dataset.collectionFilterMotionState = 'closed';
      return;
    }

    disclosure.details.dataset.collectionFilterMotionState = 'closing';
    disclosure.closeTimer = window.setTimeout(
      () => finishClose(disclosure),
      FILTER_DISCLOSURE_CLOSE_FALLBACK,
    );
  };

  const openDisclosure = (disclosure) => {
    clearScheduledMotion(disclosure);

    disclosures.forEach((candidate) => {
      if (
        candidate !== disclosure &&
        disclosure.details.dataset.collectionFilterDisclosureGroup &&
        candidate.details.dataset.collectionFilterDisclosureGroup ===
          disclosure.details.dataset.collectionFilterDisclosureGroup
      ) {
        closeDisclosure(candidate);
      }
    });

    disclosure.details.open = true;
    disclosure.motion.inert = false;

    if (
      filterReducedMotion.matches ||
      disclosure.details.dataset.collectionFilterMotionState === 'closing'
    ) {
      disclosure.details.dataset.collectionFilterMotionState = 'open';
      return;
    }

    disclosure.details.dataset.collectionFilterMotionState = 'opening';
    disclosure.motion.getBoundingClientRect();
    disclosure.frame = window.requestAnimationFrame(() => {
      disclosure.frame = 0;

      if (disclosure.details.dataset.collectionFilterMotionState === 'opening') {
        disclosure.details.dataset.collectionFilterMotionState = 'open';
      }
    });
  };

  disclosures.forEach((disclosure) => {
    disclosure.summary.addEventListener('click', (event) => {
      event.preventDefault();
      const state = disclosure.details.dataset.collectionFilterMotionState;

      if (state === 'open' || state === 'opening') {
        closeDisclosure(disclosure, { immediate: state === 'opening' });
      } else {
        openDisclosure(disclosure);
      }
    });

    disclosure.motion.addEventListener('transitionend', (event) => {
      if (event.target === disclosure.motion && event.propertyName === 'grid-template-rows') {
        finishClose(disclosure);
      }
    });
  });
};

const initializeCollectionFilterDrawer = (collection) => {
  if (!(collection instanceof HTMLElement)) {
    return;
  }

  const drawer = collection.querySelector('[data-collection-filter-drawer]');
  const initialTrigger = collection.querySelector('[data-collection-filter-open]');
  const form = drawer?.querySelector('[data-collection-filter-form]');
  const initialContent = drawer?.querySelector('.collection-filter-drawer__content');
  const closeButton = drawer?.querySelector('[data-collection-filter-close]');
  const clearButton = drawer?.querySelector('[data-collection-filter-clear]');
  const actions = drawer?.querySelector('[data-collection-filter-actions]');
  const applyButton = drawer?.querySelector('[data-collection-filter-apply]');
  const applyContent = drawer?.querySelector('[data-collection-filter-apply-content]');
  const applyLabel = drawer?.querySelector('[data-collection-filter-apply-label]');
  const zeroMessage = drawer?.querySelector('[data-collection-filter-zero-message]');
  const status = drawer?.querySelector('[data-collection-filter-status]');
  const modeTitle = drawer?.querySelector('[data-collection-filter-mode-title]');

  if (
    !(drawer instanceof HTMLDialogElement) ||
    typeof drawer.showModal !== 'function' ||
    !(initialTrigger instanceof HTMLButtonElement) ||
    !(form instanceof HTMLFormElement) ||
    !(initialContent instanceof HTMLElement) ||
    !(closeButton instanceof HTMLButtonElement) ||
    !(clearButton instanceof HTMLButtonElement) ||
    !(actions instanceof HTMLElement) ||
    !(applyButton instanceof HTMLButtonElement)
  ) {
    return;
  }

  const existingDrawer = initializedFilterDrawers.get(drawer);

  if (existingDrawer) {
    existingDrawer.bindTrigger(initialTrigger);
    return;
  }

  let trigger = initialTrigger;
  initializeFilterDisclosures(drawer);

  let activeTrigger = null;
  let closeTimer = 0;
  let drawerAnimation = null;
  let submitting = false;
  let pageScrollX = 0;
  let pageScrollY = 0;
  let resultTimer = 0;
  let resultRequest = null;
  let labelSwapRevision = 0;
  let baselineFilterSignature = '';
  let clearing = false;
  let clearRevision = 0;
  let zeroResults = false;
  let filterContent = initialContent;
  let committedFilterContent = initialContent.cloneNode(true);
  let previewFilterSignature = '';
  const defaultsToAllProducts = form.hasAttribute('data-collection-default-all-products');
  const availabilityDefaultsToAllProducts = form.hasAttribute(
    'data-collection-availability-default-all-products',
  );
  const itemNounPlural = defaultsToAllProducts ? 'products' : 'coffees';
  const previewResponseCache = createCollectionResponseCache({
    ttl: FILTER_PREVIEW_CACHE_TTL,
    maxEntries: 1,
  });

  const getAvailabilityControl = () =>
    drawer.querySelector('[data-collection-filter-availability-control]');
  const availabilityToggle = () =>
    drawer.querySelector('[data-collection-filter-availability-toggle]');
  const availabilityDefault = () =>
    drawer.querySelector('[data-collection-filter-availability-default]');

  const initializeToggleMotion = (input) => {
    if (!(input instanceof HTMLInputElement) || !input.matches('.collection-filter-toggle__input')) {
      return;
    }

    const track = input.nextElementSibling;

    if (track instanceof HTMLElement && track.matches('.collection-filter-toggle__track')) {
      track.classList.add('is-init');
    }
  };

  const setButtonLoading = (button, loading, label, { mode = 'full' } = {}) => {
    const loadingState = button.querySelector('.collection-filter-drawer__button-loading');
    const loadingLabel = button.querySelector('[data-collection-filter-loading-label]');

    if (loadingLabel instanceof HTMLElement && label) {
      loadingLabel.textContent = label;
    }

    if (loading) {
      button.setAttribute('aria-busy', 'true');
      button.dataset.collectionFilterLoadingMode = mode;
    } else {
      button.removeAttribute('aria-busy');
      delete button.dataset.collectionFilterLoadingMode;
    }
    loadingState?.setAttribute('aria-hidden', String(!loading));
  };

  const setZeroResultState = (active) => {
    zeroResults = active;
    actions.toggleAttribute('data-collection-filter-zero-results', active);

    if (active) {
      applyButton.disabled = true;
    }

    if (zeroMessage instanceof HTMLElement) {
      zeroMessage.hidden = !active;
    }
  };

  const setApplyLabel = async (text, { animate = false } = {}) => {
    if (!(applyLabel instanceof HTMLElement)) {
      return;
    }

    if (applyLabel.textContent === text) {
      if (!animate) {
        labelSwapRevision += 1;
        applyLabel.classList.remove('is-exit', 'is-enter-start');
      }

      return;
    }

    const revision = ++labelSwapRevision;
    const shouldAnimate = animate && drawer.open && !filterReducedMotion.matches;
    const previousContentRect = applyContent instanceof HTMLElement
      ? applyContent.getBoundingClientRect()
      : null;

    applyLabel.classList.remove('is-exit', 'is-enter-start');

    if (shouldAnimate) {
      applyLabel.classList.add('is-exit');
      await new Promise((resolve) => window.setTimeout(resolve, FILTER_TEXT_SWAP_DURATION));

      if (revision !== labelSwapRevision) {
        return;
      }
    }

    applyLabel.textContent = text;

    if (shouldAnimate) {
      applyLabel.classList.remove('is-exit');
      applyLabel.classList.add('is-enter-start');

      if (applyContent instanceof HTMLElement && previousContentRect) {
        const nextContentRect = applyContent.getBoundingClientRect();
        const contentOffset = previousContentRect.left - nextContentRect.left;

        applyContent.style.transition = 'none';
        applyContent.style.transform = `translate3d(${contentOffset}px, 0, 0)`;
      }

      applyLabel.getBoundingClientRect();
      window.requestAnimationFrame(() => {
        if (revision !== labelSwapRevision) {
          return;
        }

        applyLabel.classList.remove('is-enter-start');

        if (applyContent instanceof HTMLElement) {
          applyContent.style.removeProperty('transition');
          applyContent.style.transform = 'translate3d(0, 0, 0)';
        }
      });
    } else if (applyContent instanceof HTMLElement) {
      applyContent.style.removeProperty('transition');
      applyContent.style.removeProperty('transform');
    }
  };

  const setClearConfirmation = (confirming, { restoreFocus = false } = {}) => {
    actions.toggleAttribute('data-collection-filter-clear-confirming', confirming);
    clearButton.toggleAttribute('data-collection-filter-clear-confirmed', confirming);
    clearButton.setAttribute('aria-label', confirming ? 'Confirm clear filters' : 'Clear all filters');

    if (restoreFocus) {
      clearButton.focus({ preventScroll: true });
    }
  };

  const activateModePanel = (mode) => {
    const safeMode = mode === 'advanced' ? 'advanced' : 'basic';
    const inactiveMode = safeMode === 'advanced' ? 'basic' : 'advanced';
    const sharedAvailabilityControl = getAvailabilityControl();
    let inactivePanel = drawer.querySelector(`[data-collection-filter-mode-panel="${inactiveMode}"]`);

    if (inactivePanel instanceof HTMLElement) {
      let template = drawer.querySelector(`[data-collection-filter-mode-template="${inactiveMode}"]`);

      if (!(template instanceof HTMLTemplateElement)) {
        template = document.createElement('template');
        template.dataset.collectionFilterModeTemplate = inactiveMode;
        inactivePanel.before(template);
      }

      template.content.append(inactivePanel);
    }

    let panel = drawer.querySelector(`[data-collection-filter-mode-panel="${safeMode}"]`);
    if (panel instanceof HTMLElement) {
      panel.hidden = false;
      const availabilitySlot = panel.querySelector(`[data-collection-filter-availability-slot="${safeMode}"]`);
      if (sharedAvailabilityControl instanceof HTMLElement && availabilitySlot instanceof HTMLElement) {
        availabilitySlot.insertAdjacentElement('afterend', sharedAvailabilityControl);
      }
      return panel;
    }

    const targetTemplate = drawer.querySelector(`[data-collection-filter-mode-template="${safeMode}"]`);
    if (!(targetTemplate instanceof HTMLTemplateElement)) {
      return null;
    }

    targetTemplate.before(targetTemplate.content);
    panel = drawer.querySelector(`[data-collection-filter-mode-panel="${safeMode}"]`);
    if (panel instanceof HTMLElement) {
      panel.hidden = false;
      const availabilitySlot = panel.querySelector(`[data-collection-filter-availability-slot="${safeMode}"]`);
      if (sharedAvailabilityControl instanceof HTMLElement && availabilitySlot instanceof HTMLElement) {
        availabilitySlot.insertAdjacentElement('afterend', sharedAvailabilityControl);
      }
    }
    return panel;
  };

  const currentModePanel = () =>
    drawer.querySelector('[data-collection-filter-mode-panel]');

  const selectedControlCount = (container = currentModePanel()) => {
    if (!(container instanceof HTMLElement)) {
      return 0;
    }

    let selectedCount = 0;
    let priceRangeSelected = false;
    const controls = [...container.querySelectorAll('[data-collection-filter-control]:not(:disabled)')];

    if (container === currentModePanel()) {
      controls.push(...drawer.querySelectorAll('[data-collection-filter-shared-control]:not(:disabled)'));
    }

    controls.forEach((control) => {
      if (!(control instanceof HTMLInputElement)) {
        return;
      }

      if (control.matches('[data-collection-filter-price-input]')) {
        if (control.value.trim() !== '') {
          priceRangeSelected = true;
        }
        return;
      }

      if (control.type === 'checkbox' || control.type === 'radio') {
        if (
          availabilityDefaultsToAllProducts
          && control.hasAttribute('data-collection-filter-availability-toggle')
        ) {
          selectedCount += Number(!control.checked);
        } else {
          selectedCount += Number(control.checked || control.indeterminate);
        }
        return;
      }

      selectedCount += Number(control.value.trim() !== '');
    });

    return selectedCount + Number(priceRangeSelected);
  };

  const updateApplyState = ({ updateLabel = true } = {}) => {
    const selectedCount = selectedControlCount();
    const hasSelection = selectedCount > 0;
    const hasPendingChanges = filterSignature() !== baselineFilterSignature;
    const baselineIncludesSoldOut = baselineFilterSignature.split('&').includes('show_sold_out=1');
    const currentAvailabilityToggle = availabilityToggle();
    const availabilityStateChanged = currentAvailabilityToggle instanceof HTMLInputElement
      && currentAvailabilityToggle.checked !== baselineIncludesSoldOut;
    const canApply = hasPendingChanges;

    applyButton.disabled = submitting || zeroResults || !canApply;
    clearButton.disabled = submitting || (!clearing && !hasSelection);

    if (updateLabel && applyLabel instanceof HTMLElement && !submitting) {
      setApplyLabel(
        zeroResults
          ? `No ${itemNounPlural} match`
          : hasPendingChanges && availabilityStateChanged && defaultsToAllProducts
            ? currentAvailabilityToggle.checked ? `Show all ${itemNounPlural}` : `Show available ${itemNounPlural}`
          : hasPendingChanges && hasSelection
            ? `Apply ${selectedCount} ${selectedCount === 1 ? 'filter' : 'filters'}`
            : hasPendingChanges && availabilityStateChanged
              ? currentAvailabilityToggle.checked ? `Show all ${itemNounPlural}` : `Show available ${itemNounPlural}`
            : hasPendingChanges
              ? `Show all ${itemNounPlural}`
              : hasSelection
                ? 'Filters applied'
                : 'Select a filter',
      );
    }

    if (!hasSelection && !clearing) {
      setClearConfirmation(false);
    }

    return selectedCount;
  };

  const cleanFormData = () => {
    const formData = new FormData(form);

    for (const [name, value] of [...formData.entries()]) {
      if (typeof value === 'string' && value.trim() === '') {
        formData.delete(name);
      }
    }

    return formData;
  };

  const filterSignature = () =>
    [...cleanFormData().entries()]
      .filter(([name]) => name === 'show_sold_out' || (name.startsWith('filter.') && name !== 'filter.v.availability'))
      .map(([name, value]) => `${name}=${String(value)}`)
      .sort()
      .join('&');

  const searchKey = (input) => {
    const panel = input.closest('[data-collection-filter-mode-panel]')?.dataset.collectionFilterModePanel || '';
    const fieldsetLabel = input.closest('fieldset')?.querySelector(':scope > legend')?.textContent?.trim() || '';
    return `${panel}|${fieldsetLabel}`;
  };

  const disclosureKey = (details) => {
    const fieldsetLabel = details.closest('fieldset')?.querySelector(':scope > legend')?.textContent || '';
    const stableSummary = details.querySelector(
      ':scope > summary strong, :scope > summary h3, :scope > summary [data-empty-label]',
    );
    const heading = stableSummary instanceof HTMLElement
      ? stableSummary.dataset.emptyLabel || stableSummary.textContent || ''
      : details.querySelector(':scope > summary')?.textContent || '';
    const panel = details.closest('[data-collection-filter-mode-panel]')?.dataset.collectionFilterModePanel || '';
    const kind = details.hasAttribute('data-collection-filter-section-disclosure')
      ? 'section'
      : details.hasAttribute('data-collection-filter-family')
        ? 'family'
        : 'filter';
    return [panel, kind, fieldsetLabel.trim(), heading.trim()].join('|');
  };

  const contentState = () => {
    const activeElement = document.activeElement;
    const focusId = activeElement instanceof HTMLElement && filterContent.contains(activeElement)
      ? activeElement.id
      : '';
    const focusName = activeElement instanceof HTMLInputElement && filterContent.contains(activeElement)
      ? activeElement.name
      : '';
    const focusValue = activeElement instanceof HTMLInputElement && filterContent.contains(activeElement)
      ? activeElement.value
      : '';
    const activeSearchKey = activeElement instanceof HTMLInputElement
      && activeElement.matches('[data-collection-filter-search]')
      ? searchKey(activeElement)
      : '';
    const focusDisclosureKeys = [];
    let focusDisclosure = activeElement instanceof HTMLElement
      ? activeElement.closest('details')
      : null;
    const disclosureKeys = new Set();
    const searchValues = new Map();

    while (focusDisclosure instanceof HTMLDetailsElement) {
      focusDisclosureKeys.push(disclosureKey(focusDisclosure));
      focusDisclosure = focusDisclosure.parentElement?.closest('details') || null;
    }

    filterContent.querySelectorAll('details[open]').forEach((details) => {
      disclosureKeys.add(disclosureKey(details));
    });

    filterContent.querySelectorAll('[data-collection-filter-search]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) {
        return;
      }

      searchValues.set(searchKey(input), input.value);
    });

    return {
      activeSearchKey,
      disclosureKeys,
      focusDisclosureKeys,
      focusId,
      focusName,
      focusValue,
      searchSelectionEnd: activeSearchKey ? activeElement.selectionEnd : null,
      searchSelectionStart: activeSearchKey ? activeElement.selectionStart : null,
      scrollTop: drawer.scrollTop,
      searchValues,
    };
  };

  const applySearchQuery = (input) => {
    const group = input.closest('.collection-filter-group');
    const query = input.value.trim().toLowerCase();
    const items = [...(group?.querySelectorAll('[data-filter-search-item]') || [])];
    let visibleCount = 0;

    items.forEach((item) => {
      if (!(item instanceof HTMLElement)) {
        return;
      }

      const matches = !query || item.dataset.filterSearchLabel?.includes(query);
      item.hidden = !matches;
      visibleCount += Number(matches);
    });

    const empty = group?.querySelector('[data-collection-filter-search-empty]');
    if (empty instanceof HTMLElement) {
      empty.hidden = visibleCount > 0;
    }
  };

  const replaceFilterContent = (nextContent, { preserveUi = true } = {}) => {
    if (!(nextContent instanceof HTMLElement)) {
      return false;
    }

    const state = preserveUi ? contentState() : null;
    const replacement = nextContent.cloneNode(true);

    if (!(replacement instanceof HTMLElement)) {
      return false;
    }

    filterContent.replaceWith(replacement);
    filterContent = replacement;
    activateModePanel(collection.dataset.collectionBrowsingMode);

    if (state) {
      filterContent.querySelectorAll('details').forEach((details) => {
        if (details instanceof HTMLDetailsElement) {
          details.open = state.disclosureKeys.has(disclosureKey(details));
        }
      });

      filterContent.querySelectorAll('[data-collection-filter-search]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) {
          return;
        }

        input.value = state.searchValues.get(searchKey(input)) || '';
        applySearchQuery(input);
      });
    }

    initializeFilterDisclosures(filterContent);
    initializePriceSliders();
    syncMode();

    if (state) {
      drawer.scrollTop = state.scrollTop;
      let focusTarget = state.focusId ? document.getElementById(state.focusId) : null;

      if (
        !(focusTarget instanceof HTMLElement) ||
        !filterContent.contains(focusTarget) ||
        ('disabled' in focusTarget && focusTarget.disabled)
      ) {
        focusTarget = null;
      }

      if (!(focusTarget instanceof HTMLElement) && state.focusName) {
        focusTarget = [...filterContent.querySelectorAll('input')].find(
          (input) => input instanceof HTMLInputElement
            && input.name === state.focusName
            && input.value === state.focusValue
            && !input.disabled,
        );
      }

      if (!(focusTarget instanceof HTMLElement) && state.activeSearchKey) {
        focusTarget = [...filterContent.querySelectorAll('[data-collection-filter-search]')].find(
          (input) => input instanceof HTMLInputElement && searchKey(input) === state.activeSearchKey,
        );
      }

      if (!(focusTarget instanceof HTMLElement) && state.focusDisclosureKeys.length > 0) {
        const disclosures = [...filterContent.querySelectorAll('details')];
        const disclosure = state.focusDisclosureKeys
          .map((key) => disclosures.find((details) => disclosureKey(details) === key))
          .find(Boolean);
        focusTarget = disclosure?.querySelector(':scope > summary') || null;
      }

      if (focusTarget instanceof HTMLElement) {
        focusTarget.focus({ preventScroll: true });

        if (
          focusTarget instanceof HTMLInputElement &&
          focusTarget.matches('[data-collection-filter-search]') &&
          state.searchSelectionStart !== null &&
          state.searchSelectionEnd !== null
        ) {
          focusTarget.setSelectionRange(state.searchSelectionStart, state.searchSelectionEnd);
        }
      }
    }

    return true;
  };

  const restoreCommittedContent = ({ preserveUi = true } = {}) => {
    const restored = replaceFilterContent(committedFilterContent, { preserveUi });

    if (restored) {
      previewFilterSignature = baselineFilterSignature;
    }

    return restored;
  };

  const syncDirectHiddenInputs = (nextForm) => {
    form.querySelectorAll(':scope > input[type="hidden"]').forEach((input) => input.remove());
    const replacements = [...nextForm.querySelectorAll(':scope > input[type="hidden"]')]
      .map((input) => input.cloneNode(true));

    form.prepend(...replacements);
  };

  const syncCommitted = (nextCollection) => {
    const nextDrawer = nextCollection?.querySelector('[data-collection-filter-drawer]');
    const nextForm = nextDrawer?.querySelector('[data-collection-filter-form]');
    const nextContent = nextDrawer?.querySelector('.collection-filter-drawer__content');

    if (
      !(nextCollection instanceof HTMLElement) ||
      !(nextForm instanceof HTMLFormElement) ||
      !(nextContent instanceof HTMLElement)
    ) {
      return false;
    }

    resultRequest?.abort();
    resultRequest = null;
    window.clearTimeout(resultTimer);
    setButtonLoading(applyButton, false);
    setButtonLoading(clearButton, false);
    setZeroResultState(false);
    submitting = false;
    clearing = false;
    clearRevision += 1;
    form.action = nextForm.action;
    form.dataset.sectionId = nextForm.dataset.sectionId || '';
    syncDirectHiddenInputs(nextForm);
    committedFilterContent = nextContent.cloneNode(true);
    replaceFilterContent(committedFilterContent, { preserveUi: drawer.open });
    form.reset();
    syncMode();
    baselineFilterSignature = filterSignature();
    previewFilterSignature = baselineFilterSignature;
    updateApplyState();
    return true;
  };

  const updateResultCount = async ({ loadingButton = applyButton, loadingLabel = `Checking ${itemNounPlural}` } = {}) => {
    const hasPendingChanges = filterSignature() !== baselineFilterSignature;

    if (!hasPendingChanges || !(applyLabel instanceof HTMLElement)) {
      resultRequest?.abort();
      resultRequest = null;
      window.clearTimeout(resultTimer);
      setButtonLoading(applyButton, false);
      setZeroResultState(false);

      if (previewFilterSignature !== baselineFilterSignature) {
        restoreCommittedContent();
      }

      updateApplyState();
      return null;
    }

    resultRequest?.abort();
    const request = new AbortController();
    resultRequest = request;
    const requestSignature = filterSignature();
    if (loadingButton instanceof HTMLButtonElement) {
      setButtonLoading(loadingButton, true, loadingLabel, {
        mode: loadingButton === applyButton ? 'icon' : 'full',
      });
    }
    const minimumLoadingTime = loadingButton === applyButton
      ? new Promise((resolve) => {
          window.setTimeout(resolve, FILTER_RESULT_MIN_LOADING_DURATION);
        })
      : Promise.resolve();

    const url = new URL(form.action, window.location.origin);
    cleanFormData().forEach((value, name) => url.searchParams.append(name, value));
    url.searchParams.set('section_id', form.dataset.sectionId || '');

    try {
      const { response, text: html } = await fetchCollectionText(url, {
        headers: { Accept: 'text/html' },
        signal: request.signal,
      });

      if (!response.ok) {
        throw new Error(`Filter preview request failed with ${response.status}`);
      }

      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      documentFragment.querySelectorAll('noscript').forEach((node) => node.remove());
      const result = documentFragment.querySelector('[data-collection-result-count]');
      const nextContent = documentFragment.querySelector('.collection-filter-drawer__content');

      await minimumLoadingTime;

      if (resultRequest !== request || filterSignature() !== requestSignature) {
        return null;
      }

      if (!(result instanceof HTMLElement) || !(nextContent instanceof HTMLElement)) {
        throw new Error('Filter preview response was incomplete');
      }

      const resultCount = Number(result.dataset.collectionResultCount);
      const noun = resultCount === 1 ? result.dataset.resultNoun : result.dataset.resultNounPlural;
      const resultCountIsPartial = result.dataset.collectionResultCountMode === 'rendered'
        && Number(result.dataset.collectionPageCount || 1) > 1;
      const exactApplyLabel = resultCount === 0 ? `No ${itemNounPlural} match` : `Show ${resultCount} ${noun}`;

      if (Number.isFinite(resultCount) && noun) {
        previewResponseCache.set(url, html);
        replaceFilterContent(nextContent);
        previewFilterSignature = requestSignature;
        setZeroResultState(resultCount === 0);
        if (resultCountIsPartial) setZeroResultState(false);
        updateApplyState({ updateLabel: false });
        await setApplyLabel(
          resultCountIsPartial ? 'Show results' : exactApplyLabel,
          { animate: true },
        );

        if (resultRequest !== request || filterSignature() !== requestSignature) {
          return null;
        }

        if (status instanceof HTMLElement) {
          status.textContent = resultCountIsPartial
            ? `More pages may contain ready ${itemNounPlural}.`
            : resultCount === 0
            ? `No ${itemNounPlural} match. Try removing one filter.`
            : `${resultCount} ${noun} match your filters.`;
        }

        return resultCount;
      }
    } catch (error) {
      if (
        error.name !== 'AbortError' &&
        resultRequest === request &&
        filterSignature() === requestSignature
      ) {
        if (status instanceof HTMLElement) {
          status.textContent = 'Filter options could not be refreshed. Your selections are still available.';
        }
        updateApplyState();
      }
    } finally {
      if (resultRequest === request) {
        await minimumLoadingTime;

        if (resultRequest === request) {
          if (loadingButton instanceof HTMLButtonElement) {
            setButtonLoading(loadingButton, false);
          }
          resultRequest = null;
        }
      }
    }

    return null;
  };

  const scheduleResultCount = () => {
    window.clearTimeout(resultTimer);
    resultTimer = window.setTimeout(() => {
      void updateResultCount();
    }, 220);
  };

  const currentFrame = () => {
    const style = window.getComputedStyle(drawer);
    return {
      opacity: style.opacity,
      transform: style.transform === 'none' ? 'translate3d(0, 0, 0)' : style.transform,
    };
  };

  const runMotion = (opening) => {
    if (filterReducedMotion.matches || typeof drawer.animate !== 'function') {
      return null;
    }

    const interruptedFrame = drawerAnimation ? currentFrame() : null;
    drawerAnimation?.cancel();

    const from = interruptedFrame || (opening
      ? { opacity: 0, transform: `translate3d(${FILTER_DRAWER_MOTION.enterOffset}, 0, 0)` }
      : { opacity: 1, transform: 'translate3d(0, 0, 0)' });
    const to = opening
      ? { opacity: 1, transform: 'translate3d(0, 0, 0)' }
      : { opacity: 0, transform: `translate3d(${FILTER_DRAWER_MOTION.exitOffset}, 0, 0)` };

    drawerAnimation = drawer.animate([from, to], {
      duration: opening ? FILTER_DRAWER_MOTION.enterDuration : FILTER_DRAWER_MOTION.exitDuration,
      easing: opening ? FILTER_DRAWER_MOTION.enterEasing : FILTER_DRAWER_MOTION.exitEasing,
      fill: 'both',
    });

    return drawerAnimation;
  };

  const priceSliders = () =>
    [...drawer.querySelectorAll('[data-collection-filter-price-slider]')];
  const priceOutputTickers = new WeakMap();
  const initializedPriceSliders = new WeakSet();
  const priceNumberPartTypes = new Set(['minusSign', 'integer', 'group', 'decimal', 'fraction']);

  const clampPrice = (value, minimum, maximum) =>
    Math.min(Math.max(Number.isFinite(value) ? value : minimum, minimum), maximum);

  const formatPriceInput = (value) =>
    value.toFixed(2);

  const formatPriceMoney = (slider, value) => {
    const locale = slider.dataset.priceLocale || 'en-GB';
    const currency = slider.dataset.priceCurrency || 'GBP';

    try {
      const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      const parts = formatter.formatToParts(value);
      const firstNumberPart = parts.findIndex((part) => priceNumberPartTypes.has(part.type));
      let lastNumberPart = firstNumberPart;

      parts.forEach((part, index) => {
        if (priceNumberPartTypes.has(part.type)) {
          lastNumberPart = index;
        }
      });

      return {
        text: formatter.format(value),
        prefix: parts.slice(0, firstNumberPart).map((part) => part.value).join(''),
        number: parts.slice(firstNumberPart, lastNumberPart + 1).map((part) => part.value).join(''),
        suffix: parts.slice(lastNumberPart + 1).map((part) => part.value).join(''),
      };
    } catch {
      const fixedValue = value.toFixed(2);

      return {
        text: `${currency} ${fixedValue}`,
        prefix: `${currency} `,
        number: fixedValue,
        suffix: '',
      };
    }
  };

  const updatePriceOutput = (output, presentation, value, { animate = true } = {}) => {
    let ticker = priceOutputTickers.get(output);

    if (!ticker) {
      ticker = createNumberTicker(output, {
        align: output.dataset.collectionFilterPriceOutput === 'maximum' ? 'end' : 'start',
        reducedMotion: filterReducedMotion,
      });
      priceOutputTickers.set(output, ticker);
    }

    ticker.set(presentation, { animate, numericValue: value });
  };

  const syncPriceSlider = (slider, source = null) => {
    if (!(slider instanceof HTMLElement)) {
      return;
    }

    const minimumRange = slider.querySelector('[data-collection-filter-price-range="minimum"]');
    const maximumRange = slider.querySelector('[data-collection-filter-price-range="maximum"]');
    const minimumInput = slider.querySelector('[data-collection-filter-price-input="minimum"]');
    const maximumInput = slider.querySelector('[data-collection-filter-price-input="maximum"]');
    const minimumOutput = slider.querySelector('[data-collection-filter-price-output="minimum"]');
    const maximumOutput = slider.querySelector('[data-collection-filter-price-output="maximum"]');
    const minimum = Number(slider.dataset.priceMin);
    const maximum = Number(slider.dataset.priceMax);

    if (
      !(minimumRange instanceof HTMLInputElement) ||
      !(maximumRange instanceof HTMLInputElement) ||
      !(minimumInput instanceof HTMLInputElement) ||
      !(maximumInput instanceof HTMLInputElement) ||
      !Number.isFinite(minimum) ||
      !Number.isFinite(maximum) ||
      maximum <= minimum
    ) {
      return;
    }

    const sourceIsMinimum = source === minimumRange || source === minimumInput;
    const sourceIsMaximum = source === maximumRange || source === maximumInput;
    const sourceIsRange = source === minimumRange || source === maximumRange;
    let minimumValue = sourceIsRange
      ? Number(minimumRange.value)
      : minimumInput.value.trim() === ''
        ? minimum
        : Number(minimumInput.value);
    let maximumValue = sourceIsRange
      ? Number(maximumRange.value)
      : maximumInput.value.trim() === ''
        ? maximum
        : Number(maximumInput.value);

    minimumValue = clampPrice(minimumValue, minimum, maximum);
    maximumValue = clampPrice(maximumValue, minimum, maximum);

    if (minimumValue > maximumValue) {
      if (sourceIsMaximum) {
        maximumValue = minimumValue;
      } else {
        minimumValue = maximumValue;
      }
    }

    minimumRange.value = formatPriceInput(minimumValue);
    maximumRange.value = formatPriceInput(maximumValue);
    minimumRange.min = formatPriceInput(minimum);
    minimumRange.max = formatPriceInput(maximum);
    maximumRange.min = formatPriceInput(minimum);
    maximumRange.max = formatPriceInput(maximum);

    if (sourceIsRange || source === minimumInput || source === maximumInput || source === null) {
      minimumInput.value = minimumValue <= minimum ? '' : formatPriceInput(minimumValue);
      maximumInput.value = maximumValue >= maximum ? '' : formatPriceInput(maximumValue);
    }

    const range = maximum - minimum;
    const minimumPosition = ((minimumValue - minimum) / range) * 100;
    const maximumPosition = ((maximumValue - minimum) / range) * 100;
    const minimumPresentation = formatPriceMoney(slider, minimumValue);
    const maximumPresentation = formatPriceMoney(slider, maximumValue);
    const minimumFieldPresentation = {
      ...minimumPresentation,
      prefix: '',
      suffix: '',
      text: minimumPresentation.number,
    };
    const maximumFieldPresentation = {
      ...maximumPresentation,
      prefix: '',
      suffix: '',
      text: maximumPresentation.number,
    };

    slider.style.setProperty('--collection-price-min-position', `${minimumPosition}%`);
    slider.style.setProperty('--collection-price-max-position', `${maximumPosition}%`);
    minimumRange.setAttribute('aria-valuetext', minimumPresentation.text);
    maximumRange.setAttribute('aria-valuetext', maximumPresentation.text);

    if (minimumOutput instanceof HTMLOutputElement) {
      updatePriceOutput(minimumOutput, minimumFieldPresentation, minimumValue, {
        animate: sourceIsRange,
      });
    }

    if (maximumOutput instanceof HTMLOutputElement) {
      updatePriceOutput(maximumOutput, maximumFieldPresentation, maximumValue, {
        animate: sourceIsRange,
      });
    }

    if (sourceIsMinimum) {
      slider.dataset.activeThumb = 'minimum';
    } else if (sourceIsMaximum) {
      slider.dataset.activeThumb = 'maximum';
    }
  };

  const syncPriceSliders = () => {
    priceSliders().forEach((slider) => syncPriceSlider(slider));
  };

  const initializePriceSliders = () => {
    priceSliders().forEach((slider) => {
      if (!(slider instanceof HTMLElement) || initializedPriceSliders.has(slider)) {
        return;
      }

      initializedPriceSliders.add(slider);
      slider.classList.add('is-enhanced');
      slider.querySelectorAll('[data-collection-filter-price-range]').forEach((range) => {
        if (!(range instanceof HTMLInputElement)) {
          return;
        }

        const activateThumb = () => {
          slider.dataset.activeThumb = range.dataset.collectionFilterPriceRange || 'minimum';
        };

        range.addEventListener('focus', activateThumb);
        range.addEventListener('pointerdown', activateThumb);
      });
    });

    syncPriceSliders();
  };

  const updateDisclosureSummaries = () => {
    drawer.querySelectorAll('[data-collection-filter-disclosure]').forEach((disclosure) => {
      const summary = disclosure.querySelector('[data-collection-filter-summary]');
      const checked = [...disclosure.querySelectorAll('input[type="checkbox"]:checked')];

      if (!(summary instanceof HTMLElement)) {
        return;
      }

      if (checked.length === 0) {
        summary.textContent = summary.dataset.emptyLabel || 'All options';
      } else if (checked.length === 1) {
        summary.textContent = checked[0].closest('label')?.dataset.filterSummaryLabel || '1 selected';
      } else {
        summary.textContent = `${checked.length} selected`;
      }
    });
  };

  const syncSizeGroups = () => {
    drawer.querySelectorAll('[data-collection-filter-size-group]').forEach((group) => {
      const toggle = group.querySelector('[data-collection-filter-size-toggle]');
      const values = [...group.querySelectorAll('[data-collection-filter-size-value]:not(:disabled)')];

      if (!(toggle instanceof HTMLInputElement)) {
        return;
      }

      const selectedCount = values.filter((value) => value.checked).length;
      toggle.checked = selectedCount > 0;
      toggle.indeterminate = selectedCount > 0 && selectedCount < values.length;
    });
  };

  const syncValueGroups = () => {
    drawer.querySelectorAll('[data-collection-filter-value-group]').forEach((group) => {
      const toggle = group.querySelector('[data-collection-filter-value-toggle]');
      const values = [...group.querySelectorAll('[data-collection-filter-value-input]:not(:disabled)')];

      if (!(toggle instanceof HTMLInputElement)) {
        return;
      }

      const selectedCount = values.filter((value) => value.checked).length;
      toggle.checked = selectedCount > 0;
      toggle.indeterminate = selectedCount > 0 && selectedCount < values.length;
    });
  };

  const syncFamilyGroups = () => {
    drawer.querySelectorAll('[data-collection-filter-family]').forEach((family) => {
      const toggle = family.querySelector('[data-collection-filter-family-toggle]');
      const summary = family.querySelector('[data-collection-filter-family-summary]');
      const values = [...family.querySelectorAll('[data-process-family-value] [data-collection-filter-value-toggle]:not(:disabled)')];

      if (!(toggle instanceof HTMLInputElement)) {
        return;
      }

      const selectedCount = values.filter((value) => value.checked).length;
      toggle.checked = values.length > 0 && selectedCount === values.length;
      toggle.indeterminate = selectedCount > 0 && selectedCount < values.length;

      if (summary instanceof HTMLElement) {
        summary.textContent = selectedCount === 0
          ? 'None selected'
          : `${selectedCount} selected`;
      }
    });
  };

  const updateSectionSummaries = () => {
    drawer.querySelectorAll('[data-collection-filter-section-disclosure]').forEach((disclosure) => {
      const summary = disclosure.querySelector('[data-collection-filter-section-summary]');
      const selectedCount = selectedControlCount(disclosure);

      if (summary instanceof HTMLElement) {
        summary.textContent = selectedCount === 0 ? 'None selected' : `${selectedCount} selected`;
      }

      if (selectedCount > 0 && disclosure instanceof HTMLDetailsElement) {
        disclosure.open = true;
      }
    });
  };

  const syncFilterState = () => {
    const currentAvailabilityToggle = availabilityToggle();
    const currentAvailabilityDefault = availabilityDefault();
    if (
      currentAvailabilityToggle instanceof HTMLInputElement &&
      currentAvailabilityDefault instanceof HTMLInputElement
    ) {
      currentAvailabilityDefault.disabled = currentAvailabilityToggle.checked;
    }
    syncPriceSliders();
    syncSizeGroups();
    syncValueGroups();
    syncFamilyGroups();
    updateDisclosureSummaries();
    updateSectionSummaries();
    updateApplyState();
  };

  const syncMode = () => {
    const mode = defaultsToAllProducts
      ? 'basic'
      : collection.dataset.collectionBrowsingMode === 'advanced' ? 'advanced' : 'basic';
    if (defaultsToAllProducts) {
      if (collection.dataset.collectionBrowsingMode !== 'basic') {
        collection.dataset.collectionBrowsingMode = 'basic';
      }
      const filterCount = Number.parseInt(trigger.dataset.collectionFilterCount || '0', 10);
      trigger.setAttribute(
        'aria-label',
        `Filters${filterCount > 0 ? `, ${filterCount} active ${filterCount === 1 ? 'filter' : 'filters'}` : ''}`,
      );
    }
    drawer.dataset.collectionFilterMode = mode;
    const activePanel = activateModePanel(mode);

    if (activePanel instanceof HTMLElement) {
      initializeFilterDisclosures(activePanel);
      initializePriceSliders();
    }

    const availabilitySlot = drawer.querySelector(`[data-collection-filter-availability-slot="${mode}"]`);
    const availabilityControl = getAvailabilityControl();
    if (availabilityControl instanceof HTMLElement && availabilitySlot instanceof HTMLElement) {
      availabilitySlot.insertAdjacentElement('afterend', availabilityControl);
    }

    if (modeTitle instanceof HTMLElement) {
      modeTitle.textContent = defaultsToAllProducts
        ? mode === 'advanced' ? 'Fine-tune your product search' : 'Find your products'
        : mode === 'advanced' ? 'Fine-tune your coffee search' : 'Find your coffee';
    }

    if (defaultsToAllProducts) {
      drawer.querySelectorAll('.collection-filter-group__title').forEach((title) => {
        if (title.textContent.trim() === 'Roaster') title.textContent = 'Brand';
      });
      drawer.querySelectorAll('[data-collection-filter-summary]').forEach((summary) => {
        if (summary.dataset.emptyLabel === 'All roasters') summary.dataset.emptyLabel = 'All brands';
        if (summary.textContent.trim() === 'All roasters') summary.textContent = 'All brands';
      });
    }

    syncFilterState();
  };

  const finishClose = (animation = null) => {
    window.clearTimeout(closeTimer);

    if (animation && drawerAnimation !== animation) {
      return;
    }

    animation?.cancel();
    drawerAnimation = null;
    drawer.close();
    delete drawer.dataset.collectionFilterState;
    trigger.setAttribute('aria-expanded', 'false');

    if (!submitting) {
      restoreCommittedContent({ preserveUi: false });
      form.reset();
      setClearConfirmation(false);
      syncFilterState();
      activeTrigger?.focus({ preventScroll: true });
      window.scrollTo(pageScrollX, pageScrollY);
    }

    activeTrigger = null;
  };

  const closeDrawer = () => {
    if (!drawer.open || drawer.dataset.collectionFilterState === 'closing' || submitting) {
      return;
    }

    resultRequest?.abort();
    window.clearTimeout(resultTimer);
    setButtonLoading(applyButton, false);

    if (clearing) {
      clearRevision += 1;
      clearing = false;
      resultRequest?.abort();
      setButtonLoading(clearButton, false);
    }

    drawer.dataset.collectionFilterState = 'closing';
    const animation = runMotion(false);

    if (!animation) {
      finishClose();
      return;
    }

    animation.finished.then(() => finishClose(animation)).catch(() => {});
    closeTimer = window.setTimeout(
      () => finishClose(animation),
      FILTER_DRAWER_MOTION.exitDuration + 80,
    );
  };

  const openDrawer = () => {
    window.clearTimeout(closeTimer);
    activeTrigger = trigger;
    pageScrollX = window.scrollX;
    pageScrollY = window.scrollY;
    submitting = false;
    clearing = false;
    clearRevision += 1;
    setZeroResultState(false);
    setClearConfirmation(false);
    syncMode();
    baselineFilterSignature = filterSignature();
    previewFilterSignature = baselineFilterSignature;
    syncFilterState();
    scheduleResultCount();

    if (!drawer.open) {
      drawer.showModal();
    }

    delete drawer.dataset.collectionFilterState;
    trigger.setAttribute('aria-expanded', 'true');
    drawer.scrollTop = 0;
    closeButton.focus({ preventScroll: true });

    const animation = runMotion(true);
    animation?.finished
      .then(() => {
        if (drawerAnimation === animation) {
          animation.cancel();
          drawerAnimation = null;
        }
      })
      .catch(() => {});
  };

  const bindTrigger = (nextTrigger) => {
    if (!(nextTrigger instanceof HTMLButtonElement) || nextTrigger === trigger) {
      return nextTrigger === trigger;
    }

    trigger = nextTrigger;
    trigger.addEventListener('click', openDrawer);
    return true;
  };

  trigger.addEventListener('click', openDrawer);
  closeButton.addEventListener('click', closeDrawer);
  drawer.addEventListener('click', (event) => {
    if (event.target !== drawer) {
      return;
    }

    const bounds = drawer.getBoundingClientRect();
    const outsideDrawer =
      event.clientX < bounds.left ||
      event.clientX > bounds.right ||
      event.clientY < bounds.top ||
      event.clientY > bounds.bottom;

    if (outsideDrawer) {
      closeDrawer();
    }
  });

  const clearSelections = () => {
    form.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        if (input.checked) {
          initializeToggleMotion(input);
        }

        input.checked = availabilityDefaultsToAllProducts
          && input.hasAttribute('data-collection-filter-availability-toggle');
        input.indeterminate = false;
      }
    });

    form.querySelectorAll('[data-collection-filter-price-input]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.value = '';
      }
    });

    priceSliders().forEach((slider) => {
      if (!(slider instanceof HTMLElement)) {
        return;
      }

      const minimumRange = slider.querySelector('[data-collection-filter-price-range="minimum"]');
      const maximumRange = slider.querySelector('[data-collection-filter-price-range="maximum"]');

      if (minimumRange instanceof HTMLInputElement) {
        minimumRange.value = slider.dataset.priceMin || minimumRange.min;
      }
      if (maximumRange instanceof HTMLInputElement) {
        maximumRange.value = slider.dataset.priceMax || maximumRange.max;
      }
    });

    form.querySelectorAll('[data-collection-filter-search]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.value = '';
      }
    });
    form.querySelectorAll('[data-filter-search-item]').forEach((item) => {
      if (item instanceof HTMLElement) {
        item.hidden = false;
      }
    });
    form.querySelectorAll('[data-collection-filter-search-empty]').forEach((message) => {
      if (message instanceof HTMLElement) {
        message.hidden = true;
      }
    });
  };

  clearButton.addEventListener('click', async () => {
    if (clearing) {
      return;
    }

    if (!clearButton.hasAttribute('data-collection-filter-clear-confirmed')) {
      setClearConfirmation(true);

      if (status instanceof HTMLElement) {
        status.textContent = 'Press Confirm clear again to clear every selection.';
      }
      return;
    }

    clearing = true;
    const revision = ++clearRevision;
    resultRequest?.abort();
    window.clearTimeout(resultTimer);
    setClearConfirmation(false);
    setZeroResultState(false);
    setButtonLoading(clearButton, true, 'Clearing filters');
    clearSelections();
    syncFilterState();

    const minimumLoadingTime = new Promise((resolve) => {
      window.setTimeout(resolve, FILTER_ACTION_MIN_LOADING_DURATION);
    });
    const resultCountRequest = updateResultCount({ loadingButton: null });
    const [resultCount] = await Promise.all([resultCountRequest, minimumLoadingTime]);

    if (revision !== clearRevision) {
      return;
    }

    clearing = false;
    setButtonLoading(clearButton, false);
    updateApplyState({ updateLabel: resultCount === null });

    if (status instanceof HTMLElement && resultCount === null) {
      status.textContent = 'All filter selections cleared.';
    }

    if (!applyButton.disabled) {
      applyButton.focus({ preventScroll: true });
    } else {
      closeButton.focus({ preventScroll: true });
    }
  });

  const commitPriceInput = (target) => {
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-collection-filter-price-input]')) {
      return false;
    }

    setZeroResultState(false);
    syncPriceSlider(target.closest('[data-collection-filter-price-slider]'), target);
    setClearConfirmation(false);
    syncFilterState();
    scheduleResultCount();
    return true;
  };

  form.addEventListener('focusout', (event) => {
    commitPriceInput(event.target);
  });

  form.addEventListener('change', (event) => {
    const target = event.target;

    if (commitPriceInput(target)) {
      return;
    }

    setZeroResultState(false);

    if (target instanceof HTMLInputElement) {
      initializeToggleMotion(target);
    }

    if (target instanceof HTMLInputElement && target.matches('[data-collection-filter-size-toggle]')) {
      const group = target.closest('[data-collection-filter-size-group]');
      group?.querySelectorAll('[data-collection-filter-size-value]:not(:disabled)').forEach((value) => {
        if (value instanceof HTMLInputElement) {
          value.checked = target.checked;
        }
      });
    }

    if (target instanceof HTMLInputElement && target.matches('[data-collection-filter-value-toggle]')) {
      const group = target.closest('[data-collection-filter-value-group]');
      group?.querySelectorAll('[data-collection-filter-value-input]:not(:disabled)').forEach((value) => {
        if (value instanceof HTMLInputElement) {
          value.checked = target.checked;
        }
      });
    }

    if (target instanceof HTMLInputElement && target.matches('[data-collection-filter-family-toggle]')) {
      const family = target.closest('[data-collection-filter-family]');
      family?.querySelectorAll('[data-process-family-value] [data-collection-filter-value-toggle]:not(:disabled)').forEach((value) => {
        if (value instanceof HTMLInputElement) {
          value.checked = target.checked;
          const group = value.closest('[data-collection-filter-value-group]');
          group?.querySelectorAll('[data-collection-filter-value-input]:not(:disabled)').forEach((rawValue) => {
            if (rawValue instanceof HTMLInputElement) {
              rawValue.checked = target.checked;
            }
          });
        }
      });
    }

    setClearConfirmation(false);
    syncFilterState();
    scheduleResultCount();
  });

  form.addEventListener('input', (event) => {
    const target = event.target;

    if (target instanceof HTMLInputElement && target.matches('[data-collection-filter-search]')) {
      applySearchQuery(target);
      return;
    }

    if (target instanceof HTMLInputElement && target.matches('[data-collection-filter-price-range]')) {
      setZeroResultState(false);
      syncPriceSlider(target.closest('[data-collection-filter-price-slider]'), target);
      setClearConfirmation(false);
      syncFilterState();
      scheduleResultCount();
      return;
    }

  });

  form.addEventListener('submit', (event) => {
    if (submitting) {
      event.preventDefault();
      return;
    }

    const hasPendingChanges = filterSignature() !== baselineFilterSignature;

    if (!hasPendingChanges) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    const url = new URL(form.action, window.location.origin);
    cleanFormData().forEach((value, name) => url.searchParams.append(name, value));
    if (new URL(window.location.href).searchParams.get('freshest') === '1') {
      url.searchParams.set('freshest', '1');
      url.searchParams.set('sort_by', 'created-descending');
    }
    url.searchParams.delete('page');
    closeDrawer();
    submitting = true;
    const pendingResultRequest = resultRequest;
    resultRequest = null;
    pendingResultRequest?.abort();
    window.clearTimeout(resultTimer);
    drawer.setAttribute('aria-busy', 'true');
    setButtonLoading(applyButton, true, `Loading ${itemNounPlural}`);
    applyButton.disabled = true;
    clearButton.disabled = true;

    if (status instanceof HTMLElement) {
      status.textContent = 'Applying filters.';
    }

    updateCollectionView({
      collection,
      url,
      focusSelector: '[data-collection-filter-open]',
      loadingControl: trigger,
      responseCache: previewResponseCache,
    });
  });

  drawer.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeDrawer();
  });

  drawer.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusable = visibleFilterFocusables(drawer);
    const first = focusable[0];
    const last = focusable.at(-1);

    if (!first || !last) {
      return;
    }

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  drawer.addEventListener('close', () => {
    drawer.removeAttribute('aria-busy');
    setButtonLoading(applyButton, false);
    setButtonLoading(clearButton, false);
    setZeroResultState(false);
    applyButton.disabled = false;
    clearButton.disabled = false;
    trigger.setAttribute('aria-expanded', 'false');
  });

  window.addEventListener('pageshow', () => {
    submitting = false;
    clearing = false;
    clearRevision += 1;
    drawerAnimation?.cancel();
    drawerAnimation = null;
    window.clearTimeout(closeTimer);
    drawer.removeAttribute('aria-busy');
    setButtonLoading(applyButton, false);
    setButtonLoading(clearButton, false);
    setZeroResultState(false);
    setClearConfirmation(false);
    trigger.setAttribute('aria-expanded', 'false');
    form.reset();
    restoreCommittedContent({ preserveUi: false });

    form.querySelectorAll('[data-collection-filter-empty-disabled]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.disabled = false;
        delete input.dataset.collectionFilterEmptyDisabled;
      }
    });

    if (drawer.open) {
      drawer.close();
    }

    syncMode();
    baselineFilterSignature = filterSignature();
    previewFilterSignature = baselineFilterSignature;
    syncFilterState();
  });

  new MutationObserver(syncMode).observe(collection, {
    attributes: true,
    attributeFilter: ['data-collection-browsing-mode'],
  });

  initializePriceSliders();
  syncMode();
  initializedFilterDrawers.set(drawer, { bindTrigger, syncCommitted });
};

document.querySelectorAll('[data-collection-browsing]').forEach(initializeCollectionFilterDrawer);

document.addEventListener('sigma:collection-view:updated', (event) => {
  const collection = event.detail?.collection || event.target;
  initializeCollectionFilterDrawer(collection);

  if (!(collection instanceof HTMLElement)) {
    return;
  }

  const drawer = collection.querySelector('[data-collection-filter-drawer]');
  if (drawer instanceof HTMLDialogElement) {
    initializedFilterDrawers.get(drawer)?.syncCommitted(event.detail?.nextCollection);
  }
});
